/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { When } from "react-if";
import { useToggle } from "react-use";
import styled from "styled-components";
import { Box, Button, FlexContainer, theme } from "@nordcloud/gnui";
import { ExpandIcon } from "~/components";
import { isNotEmpty, isNotNil } from "~/tools";

type Props = {
  children: React.ReactNode;
  name: string;
  selectedOptions?: string[];
  isExpandedInitially?: boolean;
  onClear: () => void;
};

export function FilterWrapper({
  children,
  name,
  selectedOptions,
  isExpandedInitially,
  onClear,
}: Props) {
  const [isExpanded, toggleIsExpanded] = useToggle(
    isExpandedInitially ?? false
  );
  const isFilterSelected =
    isNotNil(selectedOptions) && isNotEmpty(selectedOptions);

  return (
    <Box
      boxStyle="lightGrey"
      marginTop={theme.spacing.spacing02}
      padding={0}
      css={{
        "&:hover": {
          backgroundColor: !isExpanded
            ? theme.color.background.ui04
            : undefined,
        },
      }}
    >
      <FilterItemWrapper
        justifyContent="space-between"
        isFilterSelected={isFilterSelected}
      >
        <FlexContainer>
          <ExpandIcon
            name="chevronDown"
            title="chevronDown"
            isExpanded={isExpanded}
            css={{
              marginRight: theme.spacing.spacing00,
            }}
            onClick={toggleIsExpanded}
          />
          {name}
        </FlexContainer>
        <FlexContainer>
          <When condition={isFilterSelected}>
            <ClearButton severity="low" onClick={onClear}>
              {`Clear: ${selectedOptions?.length ?? 0}`}
            </ClearButton>
          </When>
        </FlexContainer>
      </FilterItemWrapper>
      <When condition={isExpanded}>
        <div css={{ padding: theme.spacing.spacing02 }}>{children}</div>
      </When>
    </Box>
  );
}

const FilterItemWrapper = styled(FlexContainer)<{ isFilterSelected: boolean }>`
  height: ${theme.lineHeight};
  padding: ${theme.spacing.spacing03};
  background-color: ${({ isFilterSelected }) =>
    isFilterSelected ? theme.color.background.ui04 : undefined};
`;

const ClearButton = styled(Button)`
  background: none;
  color: ${theme.color.text.info};
  &:hover {
    background: none;
    text-decoration: underline;
    color: ${theme.color.text.info};
  }
`;
